import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { StatisticsInvoiceRow } from '../../types';
import { envConfig } from '../../config/env';
import apiClient from '../api/client';
import { delay } from '../mock/delay';
import { filterMockStatisticsRows, generateMockStatisticsRows } from '../mock/statistics';

dayjs.extend(isoWeek);

export type DatePreset = 'today' | 'week' | 'month' | 'quarter' | 'year' | 'custom';

export interface StatisticsQueryParams {
  startDate: string;
  endDate: string;
  service_name?: string;
  doctor_id?: number;
}

interface StatisticsListResponse {
  success: boolean;
  data: StatisticsInvoiceRow[];
}

export function computeDateRange(
  preset: DatePreset,
  custom: [Dayjs | null, Dayjs | null] | null
): { startDate: string; endDate: string } {
  if (preset === 'custom' && custom?.[0] && custom?.[1]) {
    return {
      startDate: custom[0].format('YYYY-MM-DD'),
      endDate: custom[1].format('YYYY-MM-DD'),
    };
  }
  const d = dayjs();
  switch (preset) {
    case 'today':
      return {
        startDate: d.startOf('day').format('YYYY-MM-DD'),
        endDate: d.endOf('day').format('YYYY-MM-DD'),
      };
    case 'week':
      return {
        startDate: d.startOf('isoWeek').format('YYYY-MM-DD'),
        endDate: d.endOf('isoWeek').format('YYYY-MM-DD'),
      };
    case 'month':
      return {
        startDate: d.startOf('month').format('YYYY-MM-DD'),
        endDate: d.endOf('month').format('YYYY-MM-DD'),
      };
    case 'quarter': {
      const m = d.month();
      const qStart = Math.floor(m / 3) * 3;
      const start = d.month(qStart).startOf('month');
      const end = d.month(qStart + 2).endOf('month');
      return {
        startDate: start.format('YYYY-MM-DD'),
        endDate: end.format('YYYY-MM-DD'),
      };
    }
    case 'year':
      return {
        startDate: d.startOf('year').format('YYYY-MM-DD'),
        endDate: d.endOf('year').format('YYYY-MM-DD'),
      };
    default:
      return {
        startDate: d.startOf('day').format('YYYY-MM-DD'),
        endDate: d.endOf('day').format('YYYY-MM-DD'),
      };
  }
}

export const statisticsService = {
  getRows: async (params: StatisticsQueryParams): Promise<StatisticsListResponse> => {
    if (envConfig.USE_STATISTICS_MOCK) {
      await delay(200);
      const all = generateMockStatisticsRows();
      const data = filterMockStatisticsRows(all, params);
      return { success: true, data };
    }
    const response = await apiClient.get<StatisticsListResponse>('/statistics/invoices/', {
      params: {
        start_date: params.startDate,
        end_date: params.endDate,
        service_name: params.service_name || undefined,
        doctor_id: params.doctor_id,
      },
    });
    return response.data;
  },
};
